import {useEffect} from 'react'

import {Brand} from '@daedalus/core/src/brand/types'

import {
  defaultLetterSpacingAdjust,
  FALLBACK_FONT_LANGUAGES,
  fontFaces,
  languageFontSettings,
  nonLatinFontFamilies
} from './constants'

type PropsType = {
  brand?: Brand
  languageCode?: string
}

const getFontFamilyCssVariable = (languageCode: string = 'en-GB') => {
  if (!FALLBACK_FONT_LANGUAGES.has(languageCode)) {
    return `"Circular", "Helvetica Neue", Helvetica, Arial, "Lucida Grande", sans-serif`
  }
  return `${nonLatinFontFamilies[languageCode as keyof typeof nonLatinFontFamilies]}, Helvetica Neue, Helvetica, Arial, "Lucida Grande", sans-serif`
}

const getFontFamilyImportPath = () => {
  const familyNames = new Set([...Object.values(nonLatinFontFamilies)])
  return Array.from(familyNames)
    .map(name => name.replace(/\s+/g, '+'))
    .join(':wght@400..500&family=')
}

const getLetterSpacingAdjustments = (languageCode: string) => {
  const adjustments = FALLBACK_FONT_LANGUAGES.has(languageCode)
    ? languageFontSettings[languageCode as keyof typeof languageFontSettings]
        .letterSpacingAdjust
    : defaultLetterSpacingAdjust
  return Object.keys(adjustments)
    .map(
      key =>
        `--letterSpacingAdjust-${key}: ${adjustments[key as keyof typeof adjustments]};`
    )
    .join('\n')
}

const getTypographyCssVariables = (languageCode: string) => {
  return `
    :root {
      --fontFamily: ${getFontFamilyCssVariable(languageCode)};
      ${getLetterSpacingAdjustments(languageCode)}
    }`
}

const fallbackFonts = `
  @import url('https://fonts.googleapis.com/css2?family=${getFontFamilyImportPath()}&display=swap');
`

export const getFontFaces = (languageCode: string) =>
  FALLBACK_FONT_LANGUAGES.has(languageCode) ? fallbackFonts : fontFaces

export const FontsLoader = ({languageCode = 'en-GB'}: PropsType) => {
  useEffect(() => {
    const styles = `${getFontFaces(languageCode)}\n${getTypographyCssVariables(languageCode)}`
    const style = document.createElement('style')
    style.innerHTML = styles
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [languageCode])

  return null
}
